import { randomUUID } from 'node:crypto';
import { execFile } from 'node:child_process';
import { resolve, basename, join } from 'node:path';
import { existsSync, mkdirSync } from 'node:fs';
import { platform, arch, release, userInfo, homedir, tmpdir } from 'node:os';
import { Agent, AgentOptions, JobRegistry, NodeDiskFilesystem, JailedFilesystem, MemFilesystem, MountFilesystem, BodDbFilesystem, toolsByName, composeHooks, lessonCapture, mkdirp, Scratch, makeAskTool, makeWebSearchAnthropicTool } from '../src/index';
import type { ChatLike, Hooks, HostBridge, RunResult, PermissionPolicy, AgentTool, IFilesystem } from '../src/index';
import { makeRealShellTool, makeShellJobTools, ShellJobRegistry, formatJobExit } from '../src/tools.shell'; // node-only; imported directly (not via index — keeps index edge-safe)
import { makeNotifyTool } from '../src/tools.notify'; // node-only, same policy
import type { OsSandboxOptions } from '../src/shell.sandbox';
import type { McpServerConfig } from '../src/mcp.client';
import { BodDB } from '@bod.ee/db'; // node-only DB backing the `--boddb` workspace mode
import { dotDirs } from './util';

/**
 * The CLI's testable core: build an Agent rooted at a real-disk cwd (jailed so secrets
 * like .env stay invisible) with the full tool set, streaming, and the hooks/host seams
 * the terminal UI uses for live display. Auto-loads .agent/{skills,commands,memory}.
 */
export interface CliOptions {
  /** Set by `runOnce`: this host runs ONE turn and then exits, so a background job cannot outlive the
   *  run (killOnExit reaps it and any late completion notice is discarded). The Shell tool's description
   *  reads it so it does not promise the model cross-turn survival it will not get. */
  singleRun?: boolean;
  ai: ChatLike;
  model?: string;
  cwd?: string;
  tools?: string[];
  stream?: boolean;
  host?: HostBridge;
  hooks?: Hooks;
  planMode?: boolean;
  permissions?: PermissionPolicy;
  maxSteps?: number;
  /** Normalized reasoning/thinking effort: 'off'|'low'|'medium'|'high' or a raw token budget. */
  reasoning?: import('../src/index').ReasoningEffort;
  subagents?: boolean;
  /** Enable sandbox background jobs (`bash({background:true})` + Job tools). Defaults ON in `--sandbox`
   *  (where there's no real shell to background); the disk path already has the real ShellJobRegistry. */
  backgroundJobs?: boolean;
  /** Root filenames loaded as project instructions (default AGENTS.md, CLAUDE.md). */
  instructionFiles?: string[];
  /** Extra tools appended to the resolved set (e.g. tools discovered from mounted MCP servers). */
  extraTools?: AgentTool[];
  /** REQUIRED — the filesystem access mode, chosen explicitly (no silent default; buildAgent THROWS
   *  if omitted). `false` = disk: the REAL filesystem with full reach, like Claude Code (root '/' is the
   *  machine root, cwd is the working dir; secrets are still hidden by the jail). `true` = sandbox: an
   *  in-memory COPY of cwd — the real disk is never modified and nothing outside cwd exists. Forcing the
   *  choice ensures a programmatic consumer always knows whether its run is sandboxed. The CLI always
   *  passes it (`--sandbox` → true, else false), so the CLI never throws. */
  sandbox: boolean;
  /** Database-backed filesystem mode: the agent's file tree lives in a persistent bod-db workspace
   *  at this directory path (a `meta.db` SQLite tree + a `files/` byte store), surviving across runs.
   *  The real disk is never touched. DB-native by default (starts with whatever the DB holds); pair
   *  with `seed` to hydrate it from cwd on the first run. Mutually exclusive with `sandbox`. */
  boddb?: string;
  /** Only with `boddb`: on the first run (empty DB), copy the cwd subtree into the DB to seed it. */
  seed?: boolean;
  /** `full` mode: add the opt-in real-shell tool (spawns /bin/sh; gated by permissions). */
  realShell?: boolean;
  /** Tier-1 hardening: OS-sandbox the real shell (sandbox-exec/bwrap) — writes confined to cwd+tmp,
   *  network deniable. Fails closed when no wrapper exists. Ignored when the real shell is off. */
  osSandbox?: boolean | Partial<OsSandboxOptions>;
  /** Extra instructions appended to the system prompt for this run (CC's --append-system-prompt). */
  appendSystemPrompt?: string;
  /** Additional real directories to mount into the VFS beyond cwd (CC's --add-dir). Ignored in sandbox. */
  addDirs?: string[];
  // kill-switches (undefined => use the Agent's built-in safety defaults)
  maxTokens?: number;
  timeoutMs?: number;
  maxRepeats?: number;
  maxToolCalls?: number;
  // context shaping (undefined => CLI default below / Agent default)
  keepToolOutputs?: number;
  maxContextTokens?: number;
  /** The MODEL's context window (tokens) — drives the context % and `/compact`'s budget. 0/unset = 200k. */
  contextWindow?: number;
  /** Forwarded to Agent.imageInput: true/false from the model catalog, undefined = unknown model. */
  imageInput?: (model: string) => boolean | undefined;
  /** Adaptive trimming that engages ONLY once a browser MCP tool is actually used (huge DOM/screenshot
   *  results blow the cumulative maxTokens budget mid-browse). Forwarded straight to Agent.browserTrim. */
  browserTrim?: { resultBytes?: number; keepOutputs?: number };
  /** Auto-capture recurring tool failures as memory lessons (needs .agent/memory). Default off — it writes files. */
  learnFromMistakes?: boolean;
  /** Configured MCP servers, forwarded to autonomous runtimes (cursor/*) for environment parity — the
   *  delegated agent sees the same MCP servers as this host. Note: env/tokens cross into the cursor
   *  subprocess (same machine/user). Ignored for chat-model providers. */
  mcpServers?: Record<string, McpServerConfig>;
  /** Names of MCP servers the HOST actually mounted (its tools reach the model as host tools, raw or via
   *  McpCall). Cursor drops natively-registered duplicates of exactly these — unmounted ones stay native. */
  hostMcpServers?: string[];
  /** Enable scratch context-offload: oversized web outputs spill to files (kept out of context,
   *  queryable via Grep/Read or the `Ask` tool). Lives in the agent's own fs, so it's ephemeral in
   *  sandbox/boddb (MemFilesystem) and persists on disk otherwise. Default off. */
  scratch?: boolean;
  /** Dir for scratch files (default `<cwd>/.agent/scratch`). The CLI passes a session-specific subdir
   *  so captures persist across resume and don't bleed between sessions. */
  scratchDir?: string;
  /** Model for the `Ask` peek (the cheap retrieve/extract tier). Default: the main model. */
  scratchAskModel?: string;
  /** Resolved Anthropic API key (config+env, env wins — same precedence as the main client). Gates and
   *  powers the default-on `WebSearchAnthropic` tool. Absent → the tool isn't registered. */
  anthropicKey?: string;
  /** Opt out of the default-on Anthropic-native search (it bills the API account per call). Default on
   *  when `anthropicKey` resolves. */
  anthropicWebSearch?: boolean;
}

/** Default CLI tool set — the structured tools the self-evolution found most efficient.
 *  ApplyEdits (cross-file atomic batch, no prior Read) + RepoMap (signature digest) were the
 *  primitives the evolve arc adopted to collapse multi-file refactors; duplex workers inherit them. */
export const DEFAULT_TOOLS = ['bash', 'Read', 'Edit', 'Write', 'Grep', 'Glob', 'MultiEdit', 'ApplyEdits', 'RepoMap', 'TodoWrite'];

/** Web tools — keyless by default (WebSearch uses DuckDuckGo; auto-upgrades by key presence:
 *  Firecrawl FIRECRAWL_API_KEY > Tavily TAVILY_API_KEY). WebSearchAnthropic (native search) is added
 *  separately in buildAgent, gated on an Anthropic key. */
function autoWebTools(): string[] {
  return ['WebFetch', 'WebSearch'];
}

/** PDF text extraction for Read on .pdf — poppler's `pdftotext <file> -` (no JS dep). Throws a
 *  readable message when poppler isn't installed; the Read tool surfaces it to the model. */
function pdfTextViaPoppler(path: string): Promise<string> {
  return new Promise((res, rej) => {
    execFile('pdftotext', [path, '-'], { maxBuffer: 32 * 1024 * 1024, timeout: 30_000 }, (e, stdout) => {
      if (e) rej(new Error(/ENOENT/.test(String((e as any).code ?? e.message)) ? 'pdftotext not installed (brew/apt install poppler)' : e.message));
      else res(stdout);
    });
  });
}

/** Directory names never copied into the sandbox VFS (heavy/derived/VCS) — keeps hydration bounded. */
const SANDBOX_SKIP = new Set(['node_modules', '.git', 'dist', 'build', '.next', 'coverage', '.cache']);

/** Copy a disk tree into another FS (bounded: skips heavy dirs). The source is jailed, so
 *  secrets are already invisible and never enter the target (in-mem sandbox or a bod-db store).
 *  Returns the file count copied. */
async function hydrate(from: IFilesystem, to: IFilesystem, dir = '/'): Promise<number> {
  let n = 0;
  for (const name of await from.readDir(dir).catch(() => [])) {
    if (SANDBOX_SKIP.has(name)) continue;
    const p = dir === '/' ? '/' + name : dir + '/' + name;
    if (await from.isDirectory(p)) { await to.createDir(p).catch(() => {}); n += await hydrate(from, to, p); }
    else { const c = await from.readFile(p).catch(() => null); if (c != null) { await to.writeFile(p, c).catch(() => {}); n++; } }
  }
  return n;
}

/**
 * Map agentx MCP config to the @cursor/sdk shape (`AgentOptions.mcpServers`) for environment parity in
 * cursor delegations. SDK accepts `{type?:'stdio',command,args,env,cwd} | {type?:'http'|'sse',url,headers}`
 * — near-identical to ours; `bearerToken` becomes an `Authorization` header. Skips disabled servers and
 * http servers whose oauth token wasn't resolved (cursor can't run the attended flow). Returns the
 * `{ mcpServers }` fragment only when non-empty, so an empty map never clobbers cursor's own ambient
 * config layers (~/.cursor/mcp.json).
 */
export function toCursorMcp(servers?: Record<string, McpServerConfig>): { mcpServers: Record<string, unknown> } | undefined {
  if (!servers) return undefined;
  const out: Record<string, unknown> = {};
  for (const [name, s] of Object.entries(servers)) {
    if (!s || s.disabled) continue;
    if (s.command) {
      out[name] = { type: 'stdio', command: s.command, ...(s.args ? { args: s.args } : {}), ...(s.env ? { env: s.env } : {}), ...(s.cwd ? { cwd: s.cwd } : {}) };
    } else if (s.url) {
      if (s.auth === 'oauth' && !s.bearerToken) continue; // unresolved oauth — cursor can't complete the flow
      const headers = { ...(s.headers ?? {}), ...(s.bearerToken ? { Authorization: `Bearer ${s.bearerToken}` } : {}) };
      out[name] = { url: s.url, ...(Object.keys(headers).length ? { headers } : {}) };
    }
  }
  return Object.keys(out).length ? { mcpServers: out } : undefined;
}

/**
 * Cursor-only providerOptions for a given model: anchors cursor to `cwd`, forwards MCP servers, and (unless
 * CURSOR_WARM=0) mints a FRESH warm-session id. Returns undefined for non-cursor models — critical because
 * the Anthropic/OpenAI adapters Object.assign providerOptions straight into the request body, so leaking
 * `cwd`/`cursorSession` to them is a hard 400 ("cwd: Extra inputs are not permitted"). Each caller (main
 * agent + every duplex worker) gets its OWN session id — sharing one across concurrent agents collides.
 * `hostMcpServers` (mounted server names) → cursor skips native registration only for those (no double MCP).
 */
export function cursorProviderOptions(model: string | undefined, cwd: string, mcpServers?: Record<string, McpServerConfig>, hostMcpServers?: string[]): Record<string, unknown> | undefined {
  if (!(model ?? '').startsWith('cursor/')) return undefined;
  return { cwd, ...(toCursorMcp(mcpServers) ?? {}), ...(hostMcpServers?.length ? { hostMcpServers } : {}), ...(process.env.CURSOR_WARM === '0' ? {} : { cursorSession: randomUUID() }) };
}

/**
 * Claude-Code SDK MCP shape — like cursor's (.mcp.json), but stdio drops `cwd` (the SDK's stdio config
 * has no such field) and http/sse REQUIRE an explicit `type` discriminator (the SDK validates it; cursor
 * tolerates its absence). Skips disabled servers and http servers whose oauth token wasn't resolved
 * (the headless SDK can't run the attended flow). Returns the `{ mcpServers }` fragment only when non-empty.
 */
export function toClaudeCodeMcp(servers?: Record<string, McpServerConfig>): { mcpServers: Record<string, unknown> } | undefined {
  if (!servers) return undefined;
  const out: Record<string, unknown> = {};
  for (const [name, s] of Object.entries(servers)) {
    if (!s || s.disabled) continue;
    if (s.command) {
      out[name] = { type: 'stdio', command: s.command, ...(s.args ? { args: s.args } : {}), ...(s.env ? { env: s.env } : {}) };
    } else if (s.url) {
      if (s.auth === 'oauth' && !s.bearerToken) continue; // unresolved oauth — the SDK can't complete the flow
      const headers = { ...(s.headers ?? {}), ...(s.bearerToken ? { Authorization: `Bearer ${s.bearerToken}` } : {}) };
      out[name] = { type: 'http', url: s.url, ...(Object.keys(headers).length ? { headers } : {}) };
    }
  }
  return Object.keys(out).length ? { mcpServers: out } : undefined;
}

/**
 * Claude-Code providerOptions: anchors the delegated CC agent to the launch `cwd` and forwards the host's
 * MCP servers (translated to the SDK shape) so it runs in the same environment — the claude-code mirror of
 * `cursorProviderOptions`. Auth is subscription-native: the adapter only injects ANTHROPIC_API_KEY when one
 * is explicitly passed, else the Agent SDK inherits the local `claude` login. Returns undefined for other
 * models (the anthropic/openai adapters Object.assign providerOptions into the request body — stray fields 400).
 */
export function claudeCodeProviderOptions(model: string | undefined, cwd: string, mcpServers?: Record<string, McpServerConfig>): Record<string, unknown> | undefined {
  if (!(model ?? '').startsWith('claude-code/')) return undefined;
  // Force the Claude SUBSCRIPTION (the whole point of claude-code/*): the SDK subprocess inherits this
  // process's env, and with ANTHROPIC_API_KEY present it would silently bill the API instead of the local
  // `claude` login. Strip the key (+ auth-token) so the Agent SDK falls back to subscription OAuth, keeping
  // the rest of the env (PATH, etc.) intact. Other models keep using ANTHROPIC_API_KEY normally — only the
  // delegated CC subprocess sees the stripped env. Opt out (bill the API via the CC runtime) with
  // AGENTX_CC_USE_API_KEY=1, which leaves the env untouched.
  // The claude.ai connector MCPs (claude_ai_*: Gmail, Drive, ...) load into every CC session from the login;
  // they are off by default so a run sees only the project's own MCPs. Opt in with ENABLE_CLAUDEAI_MCP_SERVERS=true.
  const env = (() => {
    const base = process.env.AGENTX_CC_USE_API_KEY ? process.env // inherit env as-is → API-key billing
      : (({ ANTHROPIC_API_KEY, ANTHROPIC_AUTH_TOKEN, ...rest }) => rest)(process.env);
    return { ENABLE_CLAUDEAI_MCP_SERVERS: 'false', ...base } as Record<string, string>;
  })();
  // Warm session id (default on; AGENTX_CC_WARM=0 to disable): the CC adapter reuses ONE long-lived SDK
  // subprocess across turns keyed by this id — no per-turn cold start, MCP mounted once. Per agentx
  // launch/worker (each caller gets its own id; sharing one across concurrent agents collides), mirroring
  // the cursor warm-session plumbing.
  const warm = process.env.AGENTX_CC_WARM === '0' ? {} : { claudeCodeSession: randomUUID() };
  return { cwd, env, ...warm, ...(toClaudeCodeMcp(mcpServers) ?? {}) };
}

/** providerOptions for whichever delegated runtime the model targets (cursor or claude-code); undefined otherwise. */
export function providerOptionsFor(model: string | undefined, cwd: string, mcpServers?: Record<string, McpServerConfig>, hostMcpServers?: string[]): Record<string, unknown> | undefined {
  return cursorProviderOptions(model, cwd, mcpServers, hostMcpServers) ?? claudeCodeProviderOptions(model, cwd, mcpServers);
}

export async function buildAgent(o: CliOptions): Promise<Agent> {
  // No silent default: a consumer must consciously choose disk (full real-FS reach) vs sandbox.
  if (typeof o.sandbox !== 'boolean')
    throw new Error(
      "buildAgent: explicit `sandbox` required — false = disk (the REAL filesystem, full reach like Claude Code; secrets still hidden) or true = sandbox (an in-memory copy of cwd; the real disk is never modified). This forces a conscious sandboxing decision.");
  const cwd = resolve(o.cwd ?? process.cwd());
  // Claude-Code parity: root the disk backend at the REAL filesystem root and set the working dir to
  // cwd, so absolute host paths (/Users/…) and reaching above cwd both work like a normal shell.
  // denySymlinks is OFF — at root '/', system dirs (/tmp,/var,/etc on macOS) are symlinks; the
  // escape-defense only matters for a *confined* root (evolve/library keep their own jailed cwd-root).
  // The jail still hides secrets machine-wide by name (DEFAULT_DENY: .env, .ssh, .aws, .git, keys…).
  const disk = new NodeDiskFilesystem('/', { denySymlinks: false });
  await disk.init();
  const jailedDisk = new JailedFilesystem(disk); // boundary A: hide secrets even on the real disk
  jailedDisk.setCwd(cwd);                         // working dir = launch dir (root '/' = real machine)
  // sandbox mode: agent operates over an in-memory COPY of the cwd subtree → real disk is never
  // written, and nothing outside cwd exists. Same real-path namespace as disk mode (cwd mirrored at
  // its absolute path), so paths behave identically in both modes.
  // "virtual" = a non-disk filesystem (sandbox copy or bod-db store): no real /bin/sh (it would escape
  // to disk), VFS bash + background jobs instead, and the real disk is never modified.
  const virtual = o.sandbox || !!o.boddb;
  // cursor/* is an autonomous runtime that runs its OWN tools on the real disk via @cursor/sdk
  // (Agent.create({ local: { cwd } })) — so it needs the launch cwd, and it can't honor a VFS jail.
  const isCursor = (o.model ?? '').startsWith('cursor/');
  // claude-code/* is an autonomous runtime too (@anthropic-ai/claude-agent-sdk): it runs its OWN preset
  // tools (Bash/Read/Edit/…) + MCP on the real disk inside the SDK subprocess, so agentx delegates the
  // whole turn and passes no tools of its own — the parity with cursor, minus the tool-executor (the CC
  // adapter owns its tools). Subscription-billed via the local `claude` login.
  const isClaudeCode = (o.model ?? '').startsWith('claude-code/');
  const isDelegated = isCursor || isClaudeCode;
  // Refuse sandbox/boddb for a delegated runtime rather than silently let it escape the VFS the flags promise.
  if (virtual && isDelegated)
    throw new Error(
      `${isCursor ? 'cursor' : 'claude-code'}/* models cannot run in --sandbox/--boddb: the delegated agent runs its own real-disk tools and bypasses the VFS jail. Use disk mode (default).`);
  let fs: IFilesystem = jailedDisk;
  if (o.sandbox) {
    const mem = new MemFilesystem();
    await mkdirp(mem, cwd);              // materialize the cwd path chain so the copy sits at its real path
    await hydrate(jailedDisk, mem, cwd); // copy only the cwd subtree (secrets already filtered by the jail)
    mem.setCwd(cwd);
    fs = mem;
  } else if (o.boddb) {
    // Database-backed workspace: the file tree lives in a persistent bod-db store under `o.boddb/`
    // (meta.db = the path tree, files/ = the bytes), surviving across runs. The real disk is never
    // written. DB-native by default; `--seed` hydrates the cwd subtree into the DB on the first run only.
    const root = resolve(o.boddb);
    mkdirSync(root, { recursive: true }); // parent for meta.db (storageRoot is auto-created by the VFS engine)
    const db = new BodDB({ path: join(root, 'meta.db'), sweepInterval: 0, vfs: { storageRoot: join(root, 'files') } });
    const bod = new BodDbFilesystem(db);
    const firstRun = (await bod.readDir('/').catch(() => [])).length === 0; // empty DB ⇒ never seeded
    await mkdirp(bod, cwd);              // materialize the cwd path chain so paths sit at their real namespace
    if (o.seed && firstRun) await hydrate(jailedDisk, bod, cwd); // one-time seed (secrets filtered by the jail)
    bod.setCwd(cwd);
    fs = bod;
  }
  // --add-dir: mount extra real directories into the VFS at collision-free prefixes (disk mode only —
  // a sandbox copy can't contain writes to a real mounted dir). Each is jailed like the root.
  let mountNote = '';
  if (o.addDirs?.length && !virtual) {
    const used = new Set<string>();
    const mounts = [];
    const notes: string[] = [];
    for (const d of o.addDirs) {
      const abs = resolve(d);
      let name = basename(abs) || 'dir', prefix = `/${name}`, n = 2;
      while (used.has(prefix)) prefix = `/${name}-${n++}`;
      used.add(prefix);
      const md = new NodeDiskFilesystem(abs);
      await md.init();
      mounts.push({ prefix, fs: new JailedFilesystem(md) });
      notes.push(`${prefix} → ${abs}`);
    }
    fs = new MountFilesystem(fs, mounts);
    mountNote = `Additional directories are mounted into the filesystem (read/write):\n${notes.join('\n')}\nReference files in them by their mount path (the left side).`;
  }
  // .agent dirs live under the launch dir; the FS path is cwd-anchored (root '/' = real machine
  // in disk mode; mirrored under cwd in sandbox mode). Both modes resolve to the same place.
  const dot = (sub: string) => (existsSync(`${cwd}/.agent/${sub}`) ? `${cwd}/.agent/${sub}` : undefined);
  // Search order lives in dotDirs() — shared with the REPL's catalog resolution (cli.ts adots()).
  const dots = (sub: string): string[] | undefined => {
    const dirs = dotDirs(cwd, sub, { existing: true });
    return dirs.length ? dirs : undefined;
  };
  // Memory: merged across scopes. Project dir is always first (write target, created on demand).
  // User-scope dirs are read-only fallbacks for global memories.
  const memoryDir = (() => {
    const home = homedir();
    const projectDir = `${cwd}/.agent/memory`;
    const readDirs = [
      existsSync(projectDir) ? projectDir : undefined,
      existsSync(`${cwd}/.claude/memory`) ? `${cwd}/.claude/memory` : undefined,
      existsSync(`${home}/.agent/memory`) ? `${home}/.agent/memory` : undefined,
      existsSync(`${home}/.claude/memory`) ? `${home}/.claude/memory` : undefined,
    ].filter(Boolean) as string[];
    return readDirs[0] === projectDir ? readDirs : [projectDir, ...readDirs];
  })();
  // User-scope dir for global memories (user/feedback types). First existing user-level dir wins.
  const memoryUserDir = (() => {
    const home = homedir();
    return [
      existsSync(`${home}/.agent/memory`) ? `${home}/.agent/memory` : undefined,
      existsSync(`${home}/.claude/memory`) ? `${home}/.claude/memory` : undefined,
    ].find(Boolean) ?? `${home}/.agent/memory`; // fallback: create on first write
  })();
  // opt-in: auto-capture recurring failures as persisted lessons (needs a memory dir to write to).
  const memoryWriteDir = memoryDir[0];
  const hooks = o.learnFromMistakes && memoryWriteDir ? composeHooks(o.hooks, lessonCapture({ fs, dir: memoryWriteDir, minRepeats: 2 })) : o.hooks;
  // Disk mode: real shell ON by default (CC parity — the agent should be able to run git, bun, etc).
  // Sandbox mode: real shell OFF (a real /bin/sh would escape the VFS). Explicit `realShell` overrides.
  let realShell: AgentTool[] = [];
  const agentRef: { agent?: Agent } = {};
  // ONE job registry for shell + MCP + delegated tools: one JobStatus surface, one liveness reaper.
  const jobRegistry = new JobRegistry();
  const useRealShell = o.realShell ?? !virtual;
  if (useRealShell && !virtual && !isClaudeCode) {
    // Background-job completions are pushed BACK to the model (CC parity) via agent.inject — the agent
    // isn't constructed yet, so hold it in a ref the callback closes over.
    const jobs = new ShellJobRegistry({
      cwd, killOnExit: true, osSandbox: o.osSandbox, jobs: jobRegistry,
      onExit: (n) => agentRef.agent?.inject(formatJobExit(n)),
    });
    // One-shot (`runOnce`) exits the process when the run returns, and killOnExit reaps the job — a
    // completion that lands after the run is discarded. Say so in the tool description instead of
    // promising cross-turn survival this host will not deliver.
    realShell = [makeRealShellTool({ cwd, registry: jobs, osSandbox: o.osSandbox, jobsSurviveRun: o.singleRun !== true }), ...makeShellJobTools(jobs)];
  }
  // Scratch context-offload (on by default): an oversized tool result spills to a scratch file + a
  // recoverable paginated stub instead of a lossy crop; the caller peeks with Grep/Read or Ask. Lives
  // in the agent's own fs. Disk mode → OS tmpdir (per-process, OS-reclaimed, no project litter, still
  // reachable by Read/Grep since disk fs is rooted at '/'); virtual modes → in the in-mem/db fs (ephemeral).
  const scratchDir = o.scratch ? (o.scratchDir ?? (virtual ? `${cwd}/.agent/scratch` : `${tmpdir()}/agentx-scratch-${process.pid}`)) : undefined;
  const scratch = scratchDir ? new Scratch(fs, { dir: scratchDir }) : undefined;
  return (agentRef.agent = new Agent({
    ai: o.ai,
    jobs: jobRegistry,
    fs,
    model: o.model ?? 'anthropic/claude-sonnet-4-6',
    // PDF reads (disk mode only — VFS paths aren't real files): poppler's pdftotext when installed.
    ...(!virtual ? { pdfText: pdfTextViaPoppler } : {}),
    // Anchor cursor to the launch dir (its adapter defaults to TMPDIR otherwise) and forward the
    // host's MCP servers so the delegated cursor agent runs in the same environment. Gated to cursor:
    // openai/google adapters Object.assign providerOptions into the request body, so a blanket cwd
    // would corrupt those calls.
    // Warm cursor session (default on; CURSOR_WARM=0 to disable): one long-lived agent reused across
    // turns, amortising the ~2s Agent.create + h2 handshake. Keyed per agentx launch.
    ...(() => { const po = providerOptionsFor(o.model, cwd, o.mcpServers, o.hostMcpServers); return po ? { providerOptions: po } : {}; })(),
    ...(() => {
      // Anchor the model to its working dir + tell it how the path space behaves (the fix for the
      // "host path → silently empty" trap: the model now knows whether '/' is the real machine).
      const now = new Date();
      const platformNames: Record<string, string> = { darwin: 'macOS', linux: 'Linux', win32: 'Windows' };
      const envNote = `Current date: ${now.toLocaleDateString('en-CA')} ${now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })} (${Intl.DateTimeFormat().resolvedOptions().timeZone})\nPlatform: ${platformNames[platform()] ?? platform()} ${arch()} (${release()})\nUser: ${userInfo().username}\nShell: ${process.env.SHELL ?? 'unknown'}`;
      const cwdNote = o.boddb
        ? `Working directory: ${cwd}\nDATABASE WORKSPACE: your files live in a database (bod-db), not on the real disk — the real disk is never modified and nothing outside this tree exists. Use paths relative to the working directory, or absolute paths under it.`
        : o.sandbox
        ? `Working directory: ${cwd}\nSANDBOX: you operate on an in-memory COPY of this directory — the real disk is never modified and nothing outside it exists. Use paths relative to the working directory, or absolute paths under it.`
        : `Working directory: ${cwd}\nThe filesystem root '/' is the real machine root — you have full filesystem access, like a normal shell. Use paths relative to the working directory, or absolute paths. Secret files (.env, .ssh, keys, .git) are hidden for safety.`;
      const shellNote = useRealShell && !virtual
        ? 'The `Shell` tool runs /bin/sh — you can run any installed binary (git, bun, node, curl, etc). Use `Shell` for all shell commands.'
        : undefined;
      const toneNote = 'Be concise. Do not use emojis unless the user asks. Do not explain what you are about to do — just do it.';
      // Self-awareness: agentx is its own runtime, NOT Claude Code/Desktop. Its MCP servers come from
      // .agent config (project or ~/.agent) and their tools are already mounted as `mcp__<server>__*`.
      // Without this note the model answers "what MCPs do you have?" by shelling out to read CC's
      // ~/.claude.json / claude_desktop_config.json — confusing itself with a different agent.
      const mcpNote = (() => {
        const names = new Set<string>(Object.keys(o.mcpServers ?? {}));
        for (const t of o.extraTools ?? []) if (t.name.startsWith('mcp__')) names.add(t.name.slice(5).split('__')[0]);
        const list = [...names];
        return `You are agentx (an agent runtime) — NOT Claude Code or Claude Desktop, which are separate agents with their own config. Your MCP servers are configured in .agent/settings.json or .agent/config.* (this project's .agent/ or ~/.agent/), and their tools are already in your tool list as \`mcp__<server>__<tool>\`.${list.length ? ` Configured MCP servers: ${list.join(', ')}.` : ' No MCP servers are currently configured.'} When asked which MCPs/tools you have, answer from your own tool list — never read Claude Code/Desktop config (e.g. ~/.claude.json, claude_desktop_config.json) to answer; those belong to a different agent.`;
      })();
      // Delegated runtimes (cursor/claude-code) own their tool loop, so when their run settles the turn is
      // OVER — agentx cannot make them take one more action. Composer in particular narrates as it works
      // and its run can settle mid-plan ("Also checking for X."), ending the turn on an announced action it
      // never took (~4.6% of tool-using turns in the local session corpus). The loop's closure reflex
      // catches this after the fact and costs a whole extra step; this line tries to prevent it up front.
      const closureNote = isDelegated
        ? 'Never end your turn on an action you only announced. If you say you are about to search, read, check or run something, DO it in the same turn before you stop. Your last message must be the ANSWER — the result and what it means — or an explicit statement that you are blocked and why. Narration of what you are about to do is not an ending.'
        : '';
      const extra = [o.appendSystemPrompt, envNote, cwdNote, shellNote, toneNote, mountNote, mcpNote, closureNote].filter(Boolean).join('\n\n');
      // Override the base prompt's wording when running on the real disk with a real shell.
      const basePrompt = (() => {
        let p = new AgentOptions().systemPrompt;
        if (!virtual) p = p.replace('operating on a virtual filesystem', 'operating on the host filesystem');
        if (useRealShell && !virtual) p = p.replace('Use the `bash` tool to explore (ls, grep, find, cat)', 'Use the `Shell` tool to run commands (ls, grep, find, git, etc)');
        return p;
      })();
      return { systemPrompt: basePrompt + '\n\n' + extra };
    })(),
    tools: (() => {
      // Delegated CC runtime owns its own tools inside the SDK subprocess; the adapter rejects any
      // user-defined `options.tools`. External tools reach it via providerOptions.mcpServers instead.
      if (isClaudeCode) return [];
      const requested = o.tools ?? DEFAULT_TOOLS;
      const base = toolsByName([...requested, ...autoWebTools()]);
      const tail: AgentTool[] = [...(o.extraTools ?? [])];
      // Anthropic-native search: ON by default, but ONLY when an Anthropic API key resolves — it bills
      // the API account per call (x-api-key, no subscription path), so a keyless/non-Anthropic setup must
      // not get a tool that would only ever error. Opt out via `anthropicWebSearch: false`. Skipped when
      // 'WebSearchAnthropic' is already in the requested set (the registry singleton covers that path).
      if (o.anthropicWebSearch !== false && o.anthropicKey && !requested.includes('WebSearchAnthropic')) {
        tail.push(makeWebSearchAnthropicTool({ anthropicApiKey: o.anthropicKey }));
      }
      // Scratch adds the Ask peek tool; the oversized-result spill is wired via Agent.capToolResult
      // below (one chokepoint covering EVERY tool — Grep/Read/MCP/web — not per-tool wrapping).
      if (scratch) tail.push(makeAskTool({ fs, ai: o.ai, model: o.scratchAskModel ?? o.model ?? 'anthropic/claude-sonnet-4-6', dir: scratchDir }));
      tail.push(makeNotifyTool()); // OS desktop notifications (CC PushNotification parity); degrades gracefully off macOS/Linux
      if (!realShell.length) return [...base, ...tail];
      // Real shell replaces VFS bash (having both is confusing — the model would use the wrong one).
      // The real Shell tool covers everything VFS bash does and more; structured tools (Grep/Glob/Read)
      // still operate via the VFS for consistency.
      const filtered = base.filter((t) => t.name !== 'bash');
      return [...filtered, ...realShell, ...tail];
    })(),
    maxSteps: o.maxSteps ?? 30,
    ...(o.reasoning != null ? { reasoning: o.reasoning } : {}),
    stream: o.stream ?? false,
    host: o.host,
    hooks,
    planMode: o.planMode ?? false,
    permissions: o.permissions,
    subagents: o.subagents ?? false,
    // When scratch is on, an oversized tool result spills to a scratch file + recoverable paginated stub
    // (lossless). Without scratch, the Agent's default crop (lossy) still guards the context window.
    ...(scratch ? { capToolResult: (full: string, info: { tool: string; args: any }) => scratch.spill(full, info) } : {}),
    backgroundJobs: o.backgroundJobs ?? virtual, // default ON in virtual modes (no real shell there); disk uses ShellJobRegistry
    // Subagent transcript traces, origin-anchored under .agent/traces (disk mode only — sandbox/db modes
    // must never write the real disk). Lets an interrupted/failed child leave a readable trail + pointer.
    ...(!virtual ? { traceDir: `${cwd}/.agent/traces` } : {}),
    skillsDir: dots('skills'),
    commandsDir: dots('commands'),
    memoryDir,
    memoryUserDir,
    agentsDir: dot('agents'),
    instructionFiles: o.instructionFiles ?? ['AGENTS.md', 'CLAUDE.md'],
    // Only override the Agent's safety defaults when explicitly configured.
    ...(o.maxTokens != null ? { maxTokens: o.maxTokens } : {}),
    ...(o.timeoutMs != null ? { timeoutMs: o.timeoutMs } : {}),
    ...(o.maxRepeats != null ? { maxRepeats: o.maxRepeats } : {}),
    ...(o.maxToolCalls != null ? { maxToolCalls: o.maxToolCalls } : {}),
    // Context-v2: by default, note-take over a long session (stub tool outputs older than the last 8).
    keepToolOutputs: o.keepToolOutputs ?? 8,
    ...(o.maxContextTokens != null ? { maxContextTokens: o.maxContextTokens } : {}),
    ...(o.contextWindow ? { contextWindow: o.contextWindow } : {}),
    ...(o.imageInput ? { imageInput: o.imageInput } : {}),
    ...(o.browserTrim ? { browserTrim: o.browserTrim } : {}),
  }));
}

/** Build + run a single task (fresh transcript; the cwd filesystem persists across calls). */
export async function runOnce(o: CliOptions, task: string): Promise<RunResult> {
  const agent = await buildAgent({ ...o, singleRun: true });
  return agent.run(task);
}

/** One-line human summary of a tool call for the terminal (path / command / pattern). */
export function summarizeCall(name: string, args: any): string {
  if (!args) return '';
  if (args.path) {
    // Edit: just the path — the colorized diff that follows tells the story (a 20-char «old»→«new»
    // truncates both sides to the same prefix and conveys nothing)
    if (Array.isArray(args.edits)) return `${args.path}  (${args.edits.length} edits)`;
    return String(args.path);
  }
  if (args.command) return String(args.command);
  // regex slashes only where the pattern IS a regex (Grep); Glob patterns are literal
  if (args.pattern) return name === 'Glob' ? String(args.pattern) : `/${args.pattern}/${args.glob ? ' in ' + args.glob : ''}`;
  if (args.prompt) return trunc(String(args.description ?? args.prompt), 50);
  if (args.brief) return trunc(String(args.label ? `${args.label}: ${args.brief}` : args.brief), 70); // duplex Act/Think
  return trunc(JSON.stringify(args), 60);
}
const trunc = (s: string, n: number) => (s == null ? '' : String(s).length > n ? String(s).slice(0, n) + '…' : String(s)).replace(/\n/g, '⏎');
